import React from 'react';
import { Instagram, Facebook, Twitter, Youtube, Mail, Phone, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const Footer = () => {
  const { translations } = useLanguage();

  return (
    <footer>
      {/* Share Section */}
      <div className="bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 flex justify-center items-center gap-4">
          <span className="text-sm font-medium text-gray-600">{translations.shareThisPage}</span>
          <div className="flex gap-3">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#CD0000] rounded-full flex items-center justify-center text-white hover:bg-[#c91560] transition-colors">
              <Facebook className="w-5 h-5" />
            </a>
            <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-[#1DA1F2] rounded-full flex items-center justify-center text-white hover:bg-[#1a91da] transition-colors">
              <Twitter className="w-5 h-5" />
            </a>
            <a href="mailto:talithakoumtug@gmail.com" className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center text-white hover:bg-gray-700 transition-colors">
              <Mail className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-[#D4AF37] text-[#0B0B0B] py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-12">
            {/* Column 1 - About & Social */}
            <div>
              <h3 className="text-xl font-medium mb-6 leading-relaxed">
                {translations.footerSlogan}
              </h3>
              <div className="flex gap-4 mb-6">
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#0B0B0B] rounded-full flex items-center justify-center hover:bg-[#0B0B0B] hover:text-[#D4AF37] transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://facebook.com/TalithaKoumTUG" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#0B0B0B] rounded-full flex items-center justify-center hover:bg-[#0B0B0B] hover:text-[#D4AF37] transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#0B0B0B] rounded-full flex items-center justify-center hover:bg-[#0B0B0B] hover:text-[#D4AF37] transition-colors">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-[#0B0B0B] rounded-full flex items-center justify-center hover:bg-[#0B0B0B] hover:text-[#D4AF37] transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
              <div className="space-y-2">
                <a href={`mailto:${translations.footerContact.email}`} className="flex items-center gap-2 hover:underline">
                  <Mail className="w-4 h-4" />
                  {translations.footerContact.email}
                </a>
                <a href={`tel:${translations.footerContact.phone}`} className="flex items-center gap-2 hover:underline">
                  <Phone className="w-4 h-4" />
                  {translations.footerContact.phone}
                </a>
                <p className="text-sm text-[#0B0B0B]/70 mt-2">
                  {translations.footerContact.address}
                </p>
              </div>
            </div>

            {/* Column 2 - Links */}
            <div>
              <nav className="space-y-4">
                <a href="#join" className="flex items-center gap-2 text-lg font-medium hover:underline">
                  {translations.footerLinks.joinMovement} <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#volunteer" className="flex items-center gap-2 text-lg font-medium hover:underline">
                  {translations.footerLinks.volunteer} <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#stories" className="flex items-center gap-2 text-lg font-medium hover:underline">
                  {translations.footerLinks.stories} <ArrowRight className="w-4 h-4" />
                </a>
                <a href="#research" className="flex items-center gap-2 text-lg font-medium hover:underline">
                  {translations.footerLinks.research} <ArrowRight className="w-4 h-4" />
                </a>
              </nav>
              <a
                href="#donate"
                className="mt-8 inline-flex items-center gap-2 bg-[#CD0000] text-white px-8 py-3 font-bold hover:bg-[#A00000] transition-colors"
              >
                {translations.donate || 'Donate'} <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Column 3 - Logo & Info */}
            <div className="flex flex-col items-start">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg" 
                  alt="TALITHA KOUMI TUG Logo" 
                  className="h-24 w-auto object-contain"
                />
              </div>
              <p className="text-sm text-[#0B0B0B]/80 mb-4">
                TALITHA KOUMI TUG is an international organization dedicated to empowering young girls through education, workshops, and conferences.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#B8941F] text-[#0B0B0B] py-4">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-[#0B0B0B]/80">
            {translations.copyright}
          </p>
          <nav className="flex flex-wrap justify-center gap-4 text-sm">
            <a href="#privacy" className="hover:underline">{translations.footerLegal.privacy}</a>
            <a href="#accessibility" className="hover:underline">{translations.footerLegal.accessibility}</a>
            <a href="#careers" className="hover:underline">{translations.footerLegal.careers}</a>
            <a href="#about" className="hover:underline">{translations.footerLegal.about}</a>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
